import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Logo, Icon } from './Brand'
import { roleLabel } from '../lib/user'

// A glyph per role for the sign-in buttons.
const roleGlyph = (role) => (role === 'admin' ? 'shield_person' : 'person')

export default function Login({ onSignIn }) {
  const [users, setUsers] = useState([])
  const [pending, setPending] = useState(null)
  const [error, setError] = useState(null)

  // Load the mock user directory so the buttons come from the DB, not the UI.
  useEffect(() => {
    let cancelled = false
    fetch('/api/users')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.users) setUsers(d.users) })
      .catch(() => { if (!cancelled) setError('Could not reach the backend on :8080.') })
    return () => { cancelled = true }
  }, [])

  const handle = async (user) => {
    if (pending) return
    setPending(user.user_id)
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id }),
      })
      onSignIn(res.ok ? await res.json() : user)
    } catch {
      onSignIn(user) // demo: never block sign-in on a network hiccup
    }
  }

  return (
    <div className="auth">
      {/* drifting gradient blobs */}
      <motion.div
        className="blob"
        style={{ width: 360, height: 360, background: 'var(--grad-dawn)', top: '-90px', right: '-60px', opacity: 0.5 }}
        animate={{ x: [0, 30, 0], y: [0, 24, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="blob"
        style={{ width: 280, height: 280, background: 'var(--grad-lagoon)', bottom: '-80px', left: '-50px', opacity: 0.42 }}
        animate={{ x: [0, -26, 0], y: [0, -20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />

      <motion.div
        className="auth-card"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="auth-logo">
          <Logo height={30} />
          <span className="brand-by">by Commit</span>
        </div>
        <h1>Welcome back</h1>
        <p className="sub">Choose who to sign in as</p>

        {users.length === 0 && !error && <p className="sub">Loading users…</p>}
        {error && <p className="sub">{error}</p>}

        {users.map((u) => (
          <motion.button
            key={u.user_id}
            className="sso"
            onClick={() => handle(u)}
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
          >
            <span className="gl">
              <Icon name={roleGlyph(u.role)} style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
            </span>
            {pending === u.user_id ? 'Connecting…' : `Sign in as ${roleLabel(u.role)} (${u.name})`}
            <Icon name={pending === u.user_id ? 'progress_activity' : 'arrow_forward'} className="arr" />
          </motion.button>
        ))}

        <div className="auth-foot">
          <Icon name="lock" /> Secured in your AWS perimeter · SSO enforced
        </div>
      </motion.div>
    </div>
  )
}
